import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { api } from '../../lib/api'
import ShiftBikesSnapshot, { type BikesSnapshot } from '../../components/ShiftBikesSnapshot'

interface MyShift {
  id: string
  openedAt: string
  closedAt: string | null
  status: 'open' | 'closed'
  cashAtOpen: number | null
  cashAtClose: number
  point: { id: string; name: string }
  rentalCount: number
  completedCount: number
  revenue: number
  bikesSnapshot?: BikesSnapshot | null
}

interface BikeInventory {
  id: string; code: string; type: string; model?: string | null
  status: string
  tariff?: { name: string } | null
}

const fmt = (n: number) => n.toLocaleString('ru-RU') + ' ₽'

function fmtDate(s: string) {
  return new Date(s).toLocaleDateString('ru', { day: '2-digit', month: '2-digit', year: '2-digit' })
}
function fmtTime(s: string) {
  return new Date(s).toLocaleTimeString('ru', { hour: '2-digit', minute: '2-digit' })
}

const STATUS_LABEL: Record<string, string> = { free: 'Свободен', in_use: 'В аренде', reserved: 'Забронирован', service: 'Сервис' }

export default function ManagerShiftsPage() {
  const [expandedId, setExpandedId] = useState<string | null>(null)

  const { data, isLoading } = useQuery({
    queryKey: ['my-shifts'],
    queryFn: () => api.get('/shifts/mine', { params: { limit: 60 } }).then((r) => r.data),
    staleTime: 30_000,
  })

  const shifts: MyShift[] = data?.shifts ?? []

  const expandedShift = shifts.find((s) => s.id === expandedId) ?? null
  // Skip the live fetch when a closed shift already has a stored snapshot.
  const needsLiveInventory = !!expandedShift?.point.id &&
    !(expandedShift?.status === 'closed' && expandedShift?.bikesSnapshot)
  const { data: inventoryData, isLoading: inventoryLoading } = useQuery({
    queryKey: ['bikes-at-point', expandedShift?.point.id],
    queryFn: () => api.get('/bikes', { params: { pointId: expandedShift!.point.id } }).then((r) => r.data),
    enabled: needsLiveInventory,
    staleTime: 30_000,
  })
  const inventoryBikes: BikeInventory[] = inventoryData?.bikes ?? []

  return (
    <div className="p-4 space-y-3 pb-24">
      <h2 className="text-xl font-bold">Мои смены</h2>

      {isLoading && <p className="text-gray-400 text-center py-8">Загрузка...</p>}

      {!isLoading && shifts.length === 0 && (
        <div className="text-center py-12 text-gray-400">
          <p className="text-4xl mb-2">📋</p>
          <p>Смен пока нет</p>
        </div>
      )}

      <div className="space-y-2">
        {shifts.map((s) => {
          const expanded = expandedId === s.id
          return (
            <div key={s.id} className="bg-white rounded-2xl border border-gray-200 overflow-hidden">
              <button
                type="button"
                onClick={() => setExpandedId(expanded ? null : s.id)}
                className="w-full text-left px-4 py-3"
              >
                <div className="flex justify-between items-start">
                  <div>
                    <p className="font-semibold text-sm">{s.point.name}</p>
                    <p className="text-xs text-gray-500 mt-0.5">
                      {fmtDate(s.openedAt)} {fmtTime(s.openedAt)}
                      {s.closedAt ? ` — ${fmtTime(s.closedAt)}` : ''}
                    </p>
                  </div>
                  <div className="text-right">
                    <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${s.status === 'open' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'}`}>
                      {s.status === 'open' ? 'Открыта' : 'Закрыта'}
                    </span>
                    <p className="text-sm font-bold text-green-700 mt-1">{fmt(s.revenue)}</p>
                  </div>
                </div>
              </button>

              {expanded && (
                <div className="border-t border-gray-100 px-4 py-3 space-y-1.5 bg-gray-50 text-sm">
                  <div className="flex justify-between text-xs">
                    <span className="text-gray-500">Аренд всего</span>
                    <span>{s.rentalCount}</span>
                  </div>
                  <div className="flex justify-between text-xs">
                    <span className="text-gray-500">Завершено</span>
                    <span>{s.completedCount}</span>
                  </div>
                  {s.cashAtOpen !== null && (
                    <div className="flex justify-between text-xs">
                      <span className="text-gray-500">Касса на открытии</span>
                      <span>{fmt(s.cashAtOpen)}</span>
                    </div>
                  )}
                  {s.status === 'closed' && (
                    <div className="flex justify-between text-xs">
                      <span className="text-gray-500">Касса на закрытии</span>
                      <span className="font-semibold">{fmt(s.cashAtClose)}</span>
                    </div>
                  )}
                  <div className="flex justify-between text-xs font-medium">
                    <span className="text-gray-600">Выручка</span>
                    <span className="text-green-700">{fmt(s.revenue)}</span>
                  </div>

                  {/* Inventory at point */}
                  <div className="pt-2 border-t border-gray-200">
                    <p className="text-xs font-semibold text-gray-500 mb-1.5">Инвентарь на точке</p>
                    {s.status === 'closed' && s.bikesSnapshot ? (
                      // Closed shift with a snapshot → counts as they were at close time.
                      <ShiftBikesSnapshot snapshot={s.bikesSnapshot} />
                    ) : inventoryLoading ? (
                      <p className="text-xs text-gray-400">Загрузка...</p>
                    ) : inventoryBikes.length === 0 ? (
                      <p className="text-xs text-gray-400">Нет данных</p>
                    ) : (
                      <>
                        {(['free', 'in_use', 'reserved', 'service'] as const)
                          .map((st) => {
                            const count = inventoryBikes.filter((b) => b.status === st).length
                            if (count === 0) return null
                            const colors: Record<string, string> = {
                              free: 'text-green-700', in_use: 'text-blue-700',
                              reserved: 'text-yellow-700', service: 'text-orange-700',
                            }
                            return (
                              <div key={st} className="flex justify-between text-xs">
                                <span className="text-gray-500">{STATUS_LABEL[st]}</span>
                                <span className={`font-semibold ${colors[st]}`}>{count}</span>
                              </div>
                            )
                          })}
                        <div className="flex justify-between text-xs font-semibold border-t border-gray-200 pt-1 mt-1">
                          <span className="text-gray-600">Всего</span>
                          <span>{inventoryBikes.length}</span>
                        </div>
                        {s.status === 'closed' && (
                          <p className="text-[11px] text-amber-600 mt-1">⚠️ Текущее наличие (снимок этой смены не сохранён)</p>
                        )}
                      </>
                    )}
                  </div>
                </div>
              )}
            </div>
          )
        })}
      </div>
    </div>
  )
}
